import { z } from 'zod';
import { ExternalId } from '../ingest/identity.ts';

/**
 * API-triggered campaigns (BB-101, CMP-003): the VCG backend asks a campaign to send now — to listed users with their
 * own properties (e.g. «Daily Movers», trading push) or to a segment. `Idempotency-Key` is required: the same key
 * returns the stored answer for 24 hours, and each recipient's dispatch is keyed by it, so nothing is sent twice.
 */
export const TRIGGER_MAX_RECIPIENTS = 100;
export const TRIGGER_PROPERTIES_MAX_BYTES = 50 * 1024;
export const DISPATCH_REQUEST_TTL_HOURS = 24;

const Json = z.record(z.string(), z.unknown());
const sizeOk = (value: unknown) => new TextEncoder().encode(JSON.stringify(value ?? {})).length <= TRIGGER_PROPERTIES_MAX_BYTES;

export const TriggerRecipient = z.strictObject({
  externalId: ExternalId,
  triggerProperties: Json.optional().refine(sizeOk, `trigger properties are larger than ${TRIGGER_PROPERTIES_MAX_BYTES / 1024} KB`),
  /** Applied as trusted attributes before the send (DEC-013). */
  attributes: Json.optional(),
  /** Unknown users are skipped (`not_found`) unless false — then the profile is created. */
  sendToExistingOnly: z.boolean().default(true),
});
export type TriggerRecipient = z.infer<typeof TriggerRecipient>;

export const CampaignTriggerBody = z
  .strictObject({
    recipients: z.array(TriggerRecipient).min(1).max(TRIGGER_MAX_RECIPIENTS).optional(),
    /** Broadcast to a segment instead of recipients. */
    audience: z.strictObject({ segmentApiId: z.uuid() }).optional(),
    /** Shared properties, merged under each recipient's own. */
    triggerProperties: Json.optional().refine(sizeOk, `trigger properties are larger than ${TRIGGER_PROPERTIES_MAX_BYTES / 1024} KB`),
  })
  .refine((b) => Boolean(b.recipients) !== Boolean(b.audience), { message: 'send either recipients or an audience' });
export type CampaignTriggerBody = z.infer<typeof CampaignTriggerBody>;

export const RECIPIENT_STATUSES = ['accepted', 'duplicate', 'not_found', 'invalid'] as const;

export const CampaignTriggerResponse = z.object({
  dispatchRequestId: z.uuid(),
  /** Per listed recipient; empty for an audience broadcast. */
  results: z.array(z.object({ externalId: z.string(), status: z.enum(RECIPIENT_STATUSES), detail: z.string().optional() })),
  /** Recipients queued for an audience broadcast. */
  queued: z.number().int().optional(),
});
export type CampaignTriggerResponse = z.infer<typeof CampaignTriggerResponse>;
